import { UntitledIcon, type UntitledIconData } from '@faceup/icons'
import { ulChevronRight } from '@faceup/icons/ulChevronRight'
import { ulShare03 } from '@faceup/icons/ulShare03'
import { Link, type RouteCallback } from '@faceup/router'
import { type MouseEventHandler, type Ref, useContext } from 'react'
import { twMerge } from 'tailwind-merge'
import { UiContext } from '../UiProvider/UiProvider'
import { menuItemHeight } from './adminMenuUtils'
import { type AdminMenuActivation, isKeyboardActivation } from './useAdminMenuExpansion'

type AdminMenuCountProps = {
  count: number | undefined
  className: string
  'data-test': string
}

const AdminMenuCount = ({ count, className, 'data-test': dataTest }: AdminMenuCountProps) =>
  count ? (
    <span data-test={dataTest} className={className}>
      {count > 9 ? '9+' : count}
    </span>
  ) : null

type AdminMenuItemProps = {
  itemKey: string
  label: string
  icon: UntitledIconData
  isExpanded: boolean
  isActive: boolean
  isHighlighted?: boolean
  isCurrentPage?: boolean
  isCurrentItem?: boolean
  hasSecondary?: boolean
  isLabelWrapped?: boolean
  badgeValue?: number
  isNew?: boolean
  onNavigate?: (activation: AdminMenuActivation) => void
  onMouseEnter?: () => void
  // Ant's Dropdown clones its trigger and injects the ref, the click handler and the aria state
  ref?: Ref<HTMLButtonElement>
  'aria-haspopup'?: 'menu'
  'aria-expanded'?: boolean
} & (
  | { link: RouteCallback; href?: never; onClick?: never }
  | { href: string; onClick?: MouseEventHandler<HTMLElement>; link?: never }
  | { onClick?: MouseEventHandler<HTMLElement>; link?: never; href?: never }
)

export const AdminMenuItem = ({
  itemKey,
  label,
  icon,
  isExpanded,
  isActive,
  isHighlighted,
  isCurrentPage,
  isCurrentItem,
  hasSecondary,
  isLabelWrapped,
  badgeValue,
  isNew,
  onNavigate,
  onMouseEnter,
  link,
  href,
  onClick,
  ref,
  'aria-haspopup': ariaHasPopup,
  'aria-expanded': ariaExpanded,
}: AdminMenuItemProps) => {
  const { adminMenu } = useContext(UiContext)

  const className = twMerge(
    // The item takes the rail's width rather than one of its own, so the collapse carries it along
    // instead of snapping it to the narrow size a frame before the rail starts moving
    'flex w-full cursor-pointer rounded-xl border-0 bg-transparent px-4 py-3.5 text-start transition-colors duration-150 motion-reduce:transition-none',
    isLabelWrapped ? 'items-start' : 'items-center',
    // The rail clips its overflow, so the ring has to sit inside the item or it is cut away
    'focus-visible:outline-2 focus-visible:outline-colorPrimary focus-visible:-outline-offset-2',
    isExpanded && 'gap-2',
    isActive || isHighlighted ? 'bg-colorFill' : 'hover:bg-colorFillTertiary'
  )

  const sharedProps = {
    'data-onboarding': `menu-${itemKey}`,
    'data-test': `main-menu-${itemKey}`,
    style: isLabelWrapped ? undefined : { height: menuItemHeight },
    onMouseEnter,
  }

  const firstLineAlignment = isLabelWrapped && 'flex h-5 items-center'

  const handleClick: MouseEventHandler<HTMLElement> = event => {
    onClick?.(event)
    onNavigate?.({ isKeyboard: isKeyboardActivation(event) })
  }

  const content = (
    <>
      <span
        className={twMerge(
          'flex min-w-0',
          isLabelWrapped ? 'items-start' : 'items-center',
          isExpanded && 'flex-1 gap-2'
        )}
      >
        <span className={twMerge('relative flex shrink-0 items-center', firstLineAlignment)}>
          <UntitledIcon
            icon={icon}
            size={16}
            className={twMerge('shrink-0 text-colorIconSecondary', isActive && 'text-blue5')}
          />
          {!isExpanded && (isNew || badgeValue) && (
            <span
              data-test={`main-menu-${itemKey}-badge`}
              className={twMerge(
                'absolute -end-1.5 -top-1 size-2 rounded-full border border-colorBgLayout border-solid',
                isNew ? 'bg-purple8' : 'bg-blue5'
              )}
            />
          )}
        </span>
        <span
          className={twMerge(
            'nav-reveal min-w-0 font-medium text-colorTextNavigationLabel text-sm motion-reduce:transition-none',
            isLabelWrapped ? 'break-words' : 'truncate',
            isActive && 'text-blue5',
            isExpanded && 'nav-reveal-open'
          )}
        >
          {label}
        </span>
        {isExpanded && isNew && (
          <span
            data-test={`main-menu-${itemKey}-pill`}
            className='shrink-0 rounded-full bg-purple3 px-2 py-0.5 font-semibold text-purple8 text-xs'
          >
            {adminMenu?.newBadge ?? 'New'}
          </span>
        )}
      </span>
      {isExpanded && (
        <AdminMenuCount
          count={badgeValue}
          data-test={`main-menu-${itemKey}-badge`}
          className={twMerge(
            'shrink-0 text-colorTextSecondary text-xs',
            firstLineAlignment,
            isActive && 'text-blue5'
          )}
        />
      )}
      {isExpanded && hasSecondary && (
        <UntitledIcon
          icon={ulChevronRight}
          size={16}
          rtlFlip
          className='shrink-0 text-colorTextQuaternary'
          aria-hidden
        />
      )}
      {isExpanded && href && (
        <UntitledIcon
          icon={ulShare03}
          size={16}
          className='shrink-0 text-colorIconSecondary'
          aria-hidden
        />
      )}
    </>
  )

  if (href) {
    return (
      <a
        {...sharedProps}
        className={className}
        href={href}
        onClick={handleClick}
        target='_blank'
        rel='noreferrer'
      >
        {content}
      </a>
    )
  }

  if (link) {
    return (
      <Link
        {...sharedProps}
        className={className}
        to={link}
        onClick={handleClick}
        aria-current={isCurrentPage ? 'page' : undefined}
      >
        {content}
      </Link>
    )
  }

  return (
    <button
      {...sharedProps}
      ref={ref}
      className={className}
      type='button'
      onClick={handleClick}
      aria-haspopup={ariaHasPopup}
      aria-expanded={ariaExpanded}
      aria-current={isCurrentItem ? 'true' : undefined}
    >
      {content}
    </button>
  )
}
